import type { Block, Doc, Format, Inline, List, MentionName } from "./ast";
import { safeHref } from "./links";
import { parse } from "./parse";

// A body as HTML, for the clipboard only. Copy text puts this beside the plain text, so pasting a
// formatted message into Rhema's message box, a mail app or a document keeps its formatting. Every
// piece of text is escaped and every link checked again, so the HTML holds only what the parser
// built. It is never inserted into a page.

const ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

export function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (c) => ESCAPES[c] ?? c);
}

/** Text with its line breaks kept, as a paragraph or quote writes them. */
function lines(text: string): string {
  return escapeHtml(text).replace(/\n/g, "<br>");
}

function inlineHtml(nodes: readonly Inline[]): string {
  let out = "";
  for (const n of nodes) {
    switch (n.type) {
      case "text":
        out += lines(n.text);
        break;
      case "bold":
        out += `<strong>${inlineHtml(n.children)}</strong>`;
        break;
      case "italic":
        out += `<em>${inlineHtml(n.children)}</em>`;
        break;
      case "strike":
        out += `<s>${inlineHtml(n.children)}</s>`;
        break;
      case "code":
        out += `<code>${escapeHtml(n.text)}</code>`;
        break;
      case "link": {
        const href = safeHref(n.href);
        const words = inlineHtml(n.children);
        out += href ? `<a href="${escapeHtml(href)}">${words}</a>` : words;
        break;
      }
      case "mention":
        out += escapeHtml(`@${n.name}`);
        break;
      case "channel":
        out += escapeHtml(`#${n.name}`);
        break;
    }
  }
  return out;
}

function listHtml(list: List): string {
  const tag = list.ordered ? "ol" : "ul";
  const start = list.ordered && list.start !== 1 ? ` start="${list.start}"` : "";
  const items = list.items
    .map((item) => `<li>${inlineHtml(item.children)}${item.lists.map(listHtml).join("")}</li>`)
    .join("");
  return `<${tag}${start}>${items}</${tag}>`;
}

function blockHtml(b: Block): string {
  switch (b.type) {
    case "paragraph":
      return `<p>${inlineHtml(b.children)}</p>`;
    case "quote":
      return `<blockquote>${inlineHtml(b.children)}</blockquote>`;
    case "code_block": {
      const lang = b.lang ? ` class="language-${escapeHtml(b.lang)}"` : "";
      return `<pre><code${lang}>${escapeHtml(b.text)}</code></pre>`;
    }
    case "list":
      return listHtml(b);
  }
}

/** A parsed body as HTML. */
export function docToHtml(doc: Doc): string {
  return doc.blocks.map(blockHtml).join("");
}

/** A body as escaped HTML, for putting a message on the clipboard with its formatting. */
export function toHtml(body: string, format?: Format, mentions: readonly MentionName[] = []): string {
  return docToHtml(parse(body, { format, mentions }));
}
